'use client';

import { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Chip,
  Paper
} from '@mui/material';
import {
  GroupAdd,
  Assignment,
  MobileFriendly,
  Approval,
  Dashboard,
  NotificationsActive,
  WarningAmber,
  ArrowForward,
  TableChart,
  FactCheck,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import BrandName from '../../components/BrandName';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const capabilities = [
  {
    icon: GroupAdd,
    title: 'Build the roster seamlessly',
    description: 'Add employees manually or sync rosters directly from your HR feeds, so employee lists and reporting denominators stay accurate without manual upkeep.',
  },
  {
    icon: Assignment,
    title: 'Assign questionnaires by season and policy',
    description: 'Configure vaccination types, states, and season windows; assign flu, COVID, hepatitis B, TB, and reportable-condition forms with fully customizable skip logic.',
  },
  {
    icon: MobileFriendly,
    title: 'Let employees complete forms anywhere',
    description: 'Employees use a secure email link and passcode or a self-service portal to complete questionnaires and upload documentation from any device.',
  },
  {
    icon: Approval,
    title: 'Streamline review & approval',
    description: 'Route submissions to a single review queue, with configurable auto-approve for routine cases and full documentation for exemptions.',
  },
  {
    icon: Dashboard,
    title: 'See compliance at a glance',
    description: 'Color-coded status badges for flu, COVID, TB, and reportable conditions across every employee and facility.',
  },
  {
    icon: NotificationsActive,
    title: 'Stay ahead with reminders',
    description: 'Smart nudges prompt employees with incomplete questionnaires and drive campaign completion.',
  },
];

const reporting = [
  {
    icon: Dashboard,
    title: 'Enterprise dashboards',
    description: 'Filterable by facility, state, season, market, and division — with Active Staff, Dose Due, and Unvaccinated views.',
  },
  {
    icon: TableChart,
    title: 'Real-time staff vaccination matrix',
    description: 'For employees clocked in over the last 7 days (Excel export).',
  },
  {
    icon: FactCheck,
    title: 'Surveyor-ready reports',
    description: 'Flu and COVID reports, flu compliance summaries, COVID record PDFs, and dynamic NHSN-oriented reporting.',
  },
];

export default function VaccinationTrackingPage() {
  useEffect(() => {
    document.title = 'Vaccination Tracking & Management | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Vaccination Tracking & Management"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto'
            }}
          >
            VACCINATION TRACKING & MANAGEMENT
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} />'s customizable workflow simplifies employee vaccination policies for every vaccination type — including flu, COVID-19, hepatitis B, and TB — and keeps your organization survey-ready with instant, accurate compliance reports.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem / Why Every SNF Needs This */}
      <Box sx={{ py: { xs: 4, md: 6 }, px: 4, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', p: 2, borderRadius: '50%', bgcolor: 'rgba(245, 124, 0, 0.1)', mb: 3 }}>
              <WarningAmber sx={{ fontSize: 40, color: '#f57c00' }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 4, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Compliance Challenge
            </Typography>
            <Typography sx={{ color: '#555', fontSize: { xs: '1.25rem', md: '1.4rem' }, lineHeight: 1.8, textAlign: 'center' }}>
              Healthcare employers must track and document a growing list of employee vaccinations and screenings, each with its own consent forms, exemptions, dose schedules, and state- and federal-specific rules. <BrandName variant="light" style={{ fontSize: 'inherit' }} /> replaces spreadsheets and manual chasing with one system that keeps every employee's vaccination status current, complete, and audit-ready.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* How it works / Capabilities */}
      <Box sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 }, px: 4, bgcolor: BRAND_GREEN }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            How <BrandName variant="white" style={{ fontSize: 'inherit' }} /> automates vaccination compliance
          </Typography>
          
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, 
              gap: 4,
              alignItems: 'stretch'
            }}
          >
            {capabilities.map((cap, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: 4,
                  width: '100%',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  bgcolor: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    borderColor: 'rgba(15, 76, 156, 0.3)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2,
                    bgcolor: 'rgba(15, 76, 156, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#1a1a1a' }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Visibility & reporting */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 4, bgcolor: '#fafafa' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            Visibility & reporting
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, maxWidth: 1000, mx: 'auto' }}>
            {reporting.map((rep, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    p: 4,
                    width: { xs: '100%', md: 'calc(50% - 16px)' },
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderTop: `4px solid ${BRAND_BLUE}`,
                    transition: 'all 0.3s ease',
                    bgcolor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 15px 35px rgba(15, 76, 156, 0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'rgba(15, 76, 156, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <rep.icon sx={{ color: BRAND_BLUE, fontSize: 30 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#1a1a1a' }}>
                    {rep.title}
                  </Typography>
                  <Typography sx={{ color: '#555', lineHeight: 1.6, fontSize: '1rem' }}>
                    {rep.description}
                  </Typography>
                </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Modernize Vaccination Compliance?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> automates employee vaccination compliance and keeps your organization survey-ready.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
